<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-min-h-16 tw-gap-5 text">
    <div
      class="tw-flex tw-text-[13px] tw-font-normal tw-text-[#878787] tw-min-h-5"
    >
      {{ scanTypeId[typeId] }}
    </div>

    <span class="tw-text-[13px] tw-font-medium tw-text-[#211F58]">
      {{ getComponentUiText("app-scan-scanning-types-item-tls-result-1") }}
    </span>

    <CustomTable
      :columns="tableFields"
      :data="tableData"
      :show-checkbox-row="false"
      header-wrapper-class="table-header"
      :custom-class="`tls-vulnerabilities-table-wrap scanning-type-result ${
        tableData && tableData?.length <= 5
          ? 'tw-overflow-hidden'
          : 'tw-max-h-[500px]'
      }`"
      :is-loading="isLoading"
      :hover-bg-color="'!tw-bg-[#F2F8FF80]'"
      :empty-text="
        getComponentUiText(
          'app-scan-scanning-types-item-tls-result-table-empty-1',
        )
      "
    >
      <!--      scanItem -->
      <template
        #cell(scanItem)="{
          value,
          item,
        }: {
          value: string;
          item: TypeStatusItem;
        }"
      >
        <span
          class="tw-text-[12px] tw-font-normal tw-leading-none"
          :style="`color: ${checkIsIgnored(item) ? '#DBDBDB' : '#211F58'}`"
        >
          {{ value }}
        </span>
      </template>

      <!--      scanItemSeverityId -->
      <template
        #cell(scanItemSeverityId)="{
          value,
          item,
        }: {
          value: string;
          item: TypeStatusItem;
        }"
      >
        <CellTableResultStatus
          :title="item.scanItemSeverity"
          :item-status-id="checkIsIgnored(item) ? null : value"
        />
      </template>

      <!--      scanItemStatusId -->
      <template
        #cell(scanItemStatusId)="{
          value,
          item,
        }: {
          value: string;
          item: TypeStatusItem;
        }"
      >
        <CellTableResultStatus
          :title="
            checkIsIgnored(item)
              ? getComponentUiText(
                  'app-scan-scanning-types-table-results-body-cell-unexpected-ignored-1',
                )
              : item.scanItemStatus
          "
          :item-status-id="checkIsIgnored(item) ? null : value"
        />
      </template>

      <!--      expectedResult -->
      <template
        #cell(expectedResult)="{
          item,
        }: {
          value: string;
          item: TypeStatusItem;
        }"
      >
        <CellTableResultsDetails
          v-if="item.scanItemStatusId === ItemStatus.ok || checkIsIgnored(item)"
          class-name="tw-border-r-0 tw-rounded-r-none"
          style="width: calc(100% + 14px)"
        >
          <CellDetailType1
            :title="renderValue(item.scanItemExpected)"
            :is-ignored="checkIsIgnored(item)"
            style="width: calc(200% - 14px)"
          />
        </CellTableResultsDetails>

        <CellTableResultsDetails
          v-else-if="typeof item.scanItemFindingContext === 'object'"
        >
          <CellDetailType2 :title="renderValue(item.scanItemExpected)" />
        </CellTableResultsDetails>

        <CellTableResultsDetails v-else>
          <CellDetailType4 :title="`${item.scanItemExpected || 'N/A'}`" />
        </CellTableResultsDetails>
      </template>

      <!--      unexpectedResult -->
      <template
        #cell(unexpectedResult)="{
          item,
        }: {
          value: string;
          item: TypeStatusItem;
        }"
      >
        <CellTableResultsDetails
          v-if="item.scanItemStatusId === ItemStatus.ok || checkIsIgnored(item)"
          :has-ignore="true"
          :type="getItemType(item)"
          class-name="tw-border-l-0 tw-rounded-l-none"
          @update:ignore="
            (type: keyof typeof SCANNING_TABLE_RESULT_ITEM_TYPE) =>
              handleIgnoreOrIncludeItem(item, type)
          "
        >
          <CellDetailType1
            :title="
              getType1TitleForUnexpectedResult(`${item.scanItemExpected}`)
            "
            class="tw-invisible"
          />
        </CellTableResultsDetails>

        <CellTableResultsDetails
          v-else-if="typeof item.scanItemFindingContext === 'object'"
          :has-detail="!isEmpty(item.scanItemFindingContext)"
          :has-ignore="true"
          :type="getItemType(item)"
          :is-show-detail="item.isShowDetail"
          @update:show-detail="item.isShowDetail = !item.isShowDetail"
          @update:ignore="
            (type: keyof typeof SCANNING_TABLE_RESULT_ITEM_TYPE) =>
              handleIgnoreOrIncludeItem(item, type)
          "
        >
          <CellDetailType2
            :title="renderValue(item.scanItemFound)"
            :detail="item.scanItemFindingContext"
            :is-show-detail="item.isShowDetail"
          />
        </CellTableResultsDetails>

        <CellTableResultsDetails
          v-else
          :has-detail="!isEmpty(item.scanItemFindingContext)"
          :has-ignore="true"
          :type="getItemType(item)"
          :is-show-detail="item.isShowDetail"
          @update:show-detail="item.isShowDetail = !item.isShowDetail"
          @update:ignore="
            (type: keyof typeof SCANNING_TABLE_RESULT_ITEM_TYPE) =>
              handleIgnoreOrIncludeItem(item, type)
          "
        >
          <CellDetailType4
            :title="renderValue(item.scanItemFound)"
            :detail="item.scanItemFindingContext"
            :is-show-detail="item.isShowDetail"
          />
        </CellTableResultsDetails>
      </template>
    </CustomTable>
  </div>
</template>

<script setup lang="ts">
import { computed, h, ref, watch } from "vue";
import CustomTable, {
  type CustomTableColumn,
} from "@/components/common/customTable.vue";
import { getComponentUiText } from "@/utils/utilities";
import CellTableResultsDetails from "@/components/scanning/scanningTypes/cellTableResultsDetails.vue";
import CellTableResultStatus from "@/components/scanning/scanningTypes/cellTableResultStatus.vue";
import CellDetailType1 from "@/components/scanning/scanningTypes/cellDetailType1.vue";
import CellDetailType2 from "@/components/scanning/scanningTypes/cellDetailType2.vue";
import CellDetailType4 from "@/components/scanning/scanningTypes/cellDetailType4.vue";
import { isEmpty } from "underscore";
import { IModalMessage } from "@/interfaces/common/common";
import {
  MODAL_NAME,
  SCANNING_TABLE_RESULT_ITEM_TYPE,
  TYPE_SCHEDULE,
} from "@/utils/constants";
import { ChangeEvent } from "react";
import store from "@/store";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";

type TProps = {
  isLoading?: boolean;
  data?: TypeStatusItem[];
  scanTargetType?: keyof typeof TYPE_SCHEDULE;
  rootId: string;
  typeId: string;
  scanIgnores?: {
    ignoreId: string | null;
    ignoreScanItemId: string | null;
    ignoreForWebserviceId: string | null;
    ignoreForDomainId: string | null;
    ignoreNotes: string | null;
  }[];
};

const props = defineProps<TProps>();

const scanReportItemIgnores = computed<Record<string, any>[]>(() => {
  return store.getters["Scanning/getScanReportItemIgnores"];
});

type TypeStatusItem = {
  isShowDetail?: boolean;
  scanItemId: string;
  scanItem: string;
  scanItemOrder: number;
  scanItemSeverityId: string;
  scanItemSeverity: string;
  scanItemStatusId: string | null;
  scanItemStatus: string | null;
  scanItemStatusNotes: string | null;
  scanItemExpected: string;
  scanItemExpectedSpecific: string | null;
  scanItemFound: string | null;
  scanSubReportItemFound: string | null;
  scanItemFindingContext: object | string | null;
  scanItemFindingDatetime: string | null;
  scanSourceDatatime: string | null;
  scanSourceShodanRunId: string | null;
  scanSourceCMHRunId: string | null;
  scanSourceSSLLabsRunId: string | null;
  scanSourceMozObsRunId: string | null;
  scanSourceWhoisRunId: string | null;
  scanItemPostProcessingId: string | null;
  scanSubReportRunServiceScanTypeId: string | null;
  scanSubReportRunServiceTypeId: string | null;
};

const tableData = computed(() =>
  (props.data || []).map((item) => ({
    ...item,
    id: item.scanItemId, // or use another unique field if needed
  })),
);
const reasonText = ref("");
const disableCloseIgnoreModal = ref(false);

// watch(
//   () => props.data,
//   () => {
//     tableData.value = props.data;
//   },
// );

const tableFields: CustomTableColumn[] = [
  {
    text: getComponentUiText(
      "app-scan-scanning-types-table-results-header-cell-scan-item-1",
    ),
    value: "scanItem",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
    valueClass: "!tw-pr-4 !tw-align-top tw-w-[25%]",
  },
  {
    text: getComponentUiText(
      "app-scan-scanning-types-table-results-header-cell-severity-1",
    ),
    value: "scanItemSeverityId",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase tw-justify-center",
    sortable: false,
    valueClass: "!tw-pr-4 !tw-align-top",
  },
  {
    text: getComponentUiText(
      "app-scan-scanning-types-table-results-header-cell-status-1",
    ),
    value: "scanItemStatusId",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase tw-justify-center",
    sortable: false,
    valueClass: "!tw-pr-4 !tw-align-top",
  },
  {
    text: getComponentUiText(
      "app-scan-scanning-types-table-results-header-cell-expected-result-1",
    ),
    value: "expectedResult",
    headerClass:
      "!tw-pr-[14px] !tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
    valueClass: "!tw-pr-[14px] !tw-align-top tw-w-[25%]",
  },
  {
    text: getComponentUiText(
      "app-scan-scanning-types-table-results-header-cell-unexpected-result-1",
    ),
    value: "unexpectedResult",
    headerClass:
      "!tw-text-[#fff] tw-text-[10px] tw-not-italic tw-font-semibold tw-leading-[100%] tw-tracking-[1px] tw-uppercase",
    sortable: false,
    valueClass: "!tw-align-top tw-w-[25%]",
  },
];
const scanTypeId = {
  domain: getComponentUiText("app-scan-scanning-types-description-dns-1"),
  net: getComponentUiText(
    "app-scan-scanning-types-description-network-security-1",
  ),
  https: getComponentUiText("app-scan-scanning-types-description-tls-1"),
  tls_ssl: getComponentUiText("app-scan-scanning-types-description-ssl-1"),
  http_headers: getComponentUiText(
    "app-scan-scanning-types-description-http-headers-1",
  ),
  html: getComponentUiText("app-scan-scanning-types-description-html-1"),
};
const ItemStatus = {
  ok: "ok",
  na: null,
  partial: "par",
  no: "no",
  nap: "nap",
};

const getType1TitleForUnexpectedResult = (text: string) => {
  if (!text) {
    return "";
  }
  return text.substring(0, text.length / 2 - 7);
};

const renderValue = (value: any) => {
  return value === null ? "N/A" : `${value}`;
};

const getItemType = (item: TypeStatusItem) => {
  const hasIncludeItem = scanReportItemIgnores.value.some((scan) => {
    const parentId =
      props.scanTargetType === TYPE_SCHEDULE.webservices
        ? scan.ignoreForWebserviceId
        : scan.ignoreForDomainId;
    return (
      Number(parentId) === Number(props.rootId) &&
      scan.ignoreScanItemId === item.scanItemId &&
      !scan.isArchived
    );
  });
  return hasIncludeItem
    ? SCANNING_TABLE_RESULT_ITEM_TYPE.include
    : SCANNING_TABLE_RESULT_ITEM_TYPE.ignore;
};

const checkIsIgnored = (item: TypeStatusItem) => {
  return Boolean(item.scanItemPostProcessingId !== null);
};

const handleIgnoreOrIncludeItem = (
  item: TypeStatusItem,
  type: keyof typeof SCANNING_TABLE_RESULT_ITEM_TYPE,
) => {
  const isIgnore = type === SCANNING_TABLE_RESULT_ITEM_TYPE.ignore;
  disableCloseIgnoreModal.value = false;

  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      headerTitle: isIgnore
        ? getComponentUiText(
            "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-header-1",
          )
        : "",
      title: getComponentUiText(
        isIgnore
          ? "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-title-1"
          : "app-scan-scanning-types-table-results-body-cell-item-modal-include-title-1",
      ),
      description: getComponentUiText(
        isIgnore
          ? "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-description-1"
          : "app-scan-scanning-types-table-results-body-cell-item-modal-include-description-1",
      ),
      list: [],
      disableCloseModal: disableCloseIgnoreModal,
      slotCustom: h(
        "div",
        {
          class:
            "tw-flex tw-flex-1 tw-flex-col tw-gap-5 tw-text-[#231F20] tw-text-[14px]",
        },
        [
          h(
            "span",
            {},
            getComponentUiText(
              isIgnore
                ? "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-description-2"
                : "app-scan-scanning-types-table-results-body-cell-item-modal-include-description-2",
            ),
          ),
          h("input", {
            type: "text",
            placeholder: getComponentUiText(
              "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-input-reason-placeholder-1",
            ),
            class:
              "tw-w-full tw-p-3 tw-rounded-lg tw-border tw-border-solid tw-border-[#EFF0F1] tw-bg-[rgba(239,240,241,0.5)]",
            onInput(event: ChangeEvent<HTMLInputElement>) {
              reasonText.value = event.target.value;
              reasonText.value && (disableCloseIgnoreModal.value = true);
              !reasonText.value && (disableCloseIgnoreModal.value = false);
            },
          }),
        ],
      ),
      buttonText: getComponentUiText(
        isIgnore
          ? "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-button-ok-1"
          : "app-scan-scanning-types-table-results-body-cell-item-modal-include-button-ok-1",
      ),
      buttonRed: false,
      hiddenHelp: false,
      updateDataFunction: async () => {
        return await handleIgnore(item, isIgnore);
      },
    },
    events: {
      "update:data": () => {},
      "update:close": () => {
        reasonText.value = "";
      },
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const handleIgnore = async (item, isIgnore) => {
  const defaultNote = getComponentUiText(
    "app-scan-scanning-types-table-results-body-cell-item-modal-ignore-reason-default-1",
  );
  let returnValue;
  const data: any = {
    scanTypeId: item.scanSubReportRunServiceScanTypeId,
    scanItemId: item.scanItemId,
    notes: reasonText.value || (isIgnore ? defaultNote : ""),
    [props.scanTargetType === TYPE_SCHEDULE.webservices
      ? "webserviceId"
      : "domainId"]: props.rootId,
  };
  if (isIgnore) {
    returnValue = await store.dispatch(
      "Scanning/handleIgnoreScanReportItem",
      data,
    );
    reasonText.value = "";
    return returnValue;
  }
  const ignoreItemIds = item.scanItemPostProcessingId
    ? [item.scanItemPostProcessingId]
    : [
        scanReportItemIgnores.value.find(
          (scan) => scan.ignoreScanItemId === item.scanItemId,
        )?.ignoreId,
      ];
  returnValue = await store.dispatch("Scanning/handleIncludeScanReportItem", {
    data,
    ignoreItemIds,
  });
  reasonText.value = "";
  return returnValue;
};
</script>

<style scoped lang="scss">
:deep(.tls-vulnerabilities-table-wrap) {
  box-shadow: none !important;
}
:deep(text) {
  font-family: Poppins, sans-serif;
}
:deep(.table-header) {
  height: 46px;
  background-color: #211f58 !important;
  &:first-of-type {
    padding-left: 25px !important;
  }
}
:deep(.table-data) {
  padding: 14px 14px 14px 0;
  &:first-of-type {
    padding-left: 25px !important;
  }
}
</style>
